import { useEffect, useRef } from "react";
import { useNavigationState } from "@/zustand/NavigationState";
import { useConnectionState } from "@/zustand/ConnectionState";
import { featureFlags } from "@/config/featureFlags";
import {
  getChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { addLinksToLocalDB } from "@/indexedDB/services/links";
import { addMessagesToLocalDB } from "@/indexedDB/services/messages";
import { LinkEntity, MessageEntity } from "@/indexedDB/types";
import { isUserAuthenticated } from "@/middleware/auth";
import { processMessageLink } from "@/middleware/links";
// import { processMessages, processParentMessage } from "@/middleware/messages";
import { createLogger } from "@/utils/debug";
import {
  umbra_processMessages,
  umbra_processParentMessage,
} from "@/middleware/umbra_messages";

// const TAKE = 50; // on UMBRA  "take" is hardocoded to 200 messages per request

// Create a logger for reconciliation operations
const log = createLogger("Reconciliation");

/**
 * Purpose: Manages forced reconciliation of messages for active chats
 *
 * How it works:
 * 1. Tracks which chat IDs have already been reconciled
 * 2. Triggers reconciliation when a new chat becomes active
 * 3. Resets reconciliation state when connection status changes
 *
 * Key features:
 * - Prevents duplicate reconciliation requests for the same chat
 * - Automatically resets when connection is re-established
 * - Uses Set for efficient lookups and better performance
 */
export function useForcedReconciliation(): void {
  // Using a ref instead of state to avoid unnecessary re-renders
  // since we only use this for lookups and don't need to trigger renders on changes
  const reconciledChatIds = useRef<Set<string>>(new Set());
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const lastOnlineTimestamp = useConnectionState(
    (state) => state.lastOnlineTimestamp,
  );

  /**
   * Reset reconciled chats when connection is re-established
   * This ensures we get fresh data after a reconnection
   */
  useEffect(() => {
    if (lastOnlineTimestamp) {
      reconciledChatIds.current.clear();
    }
    // No cleanup needed as this only runs when lastOnlineTimestamp changes
  }, [lastOnlineTimestamp]);

  /**
   * Trigger reconciliation for newly activated chats
   * Only reconciles each chat once to prevent duplicate requests
   *
   * Skipped when the Phase 4 sync engine is active — it handles
   * message reconciliation via the events endpoint. Keeping the hook
   * mounted (effects still run for the reset above) so that turning
   * the flag off restores legacy behavior without code changes.
   * TODO: Phase 5 — remove this hook entirely once the new sync
   * engine is proven stable.
   */
  useEffect(() => {
    // When the new sync engine is active, skip legacy per-chat reconciliation
    // to prevent double-syncing (CRITICAL 4).
    if (featureFlags.useNewSync) {
      return;
    }

    // Skip if no active chat or if already reconciled
    if (!activeChatId) {
      return;
    }

    if (reconciledChatIds.current.has(activeChatId)) {
      return;
    }

    // Trigger reconciliation and only mark as reconciled if successful
    forceReconciliation(activeChatId)
      .then((result) => {
        // Only mark as reconciled if we got a successful response or found no messages
        // (which is a valid state, not an error)
        if (result.status === "success" || result.status === "no_messages") {
          reconciledChatIds.current.add(activeChatId);
          log.info(`Chat ${activeChatId} successfully reconciled`);
        } else {
          log.warn(`Chat ${activeChatId} not found, may retry later`);
        }
      })
      .catch((error) => {
        // Don't mark as reconciled if there was an error, so we can retry later
        log.error(`Failed to reconcile chat ${activeChatId}`, error);
      });

    // No cleanup needed as this is idempotent
  }, [activeChatId]); // Removed ids from dependency array to prevent unnecessary re-renders
}

/**
 * Purpose: Synchronizes messages from the server to the local database for a specific chat
 *
 * How it works:
 * 1. Validates user authentication
 * 2. Retrieves chat data from local database
 * 3. Fetches latest messages from the server
 * 4. Processes parent messages for replies
 * 5. Processes link metadata for messages with links
 * 6. Updates local database with all collected data
 *
 * Key features:
 * - Handles authentication verification
 * - Processes parent messages for replies
 * - Processes link metadata
 * - Updates local database with synchronized data
 *
 * @param chatId - The ID of the chat to reconcile
 * @returns Promise with reconciliation summary
 * @throws Error if authentication is missing or message loading fails
 */
export const forceReconciliation = async (
  chatId: string,
): Promise<ReconciliationResponse> => {
  log.info(`Starting for chat: ${chatId}`);

  // Validate authentication before proceeding
  validateAuthentication();

  try {
    // Get chat details from local DB
    const localDbChat = await getChatDetails(chatId);
    if (!localDbChat) {
      return { status: "not_found", chatId };
    }

    // Fetch messages from server
    // const messages = await fetchMessages(chatId, localDbChat.eCSK);
    const messages = await fetchMessages(chatId, localDbChat.eCSK);
    if (messages.length === 0) {
      return { status: "no_messages", chatId };
    }

    // Save messages to local DB
    await saveMessagesToLocalDB(messages, chatId);

    // Identify messages that need additional processing
    const messagesToProcessParent =
      identifyMessagesNeedingParentProcessing(messages);
    const messagesToProcessLinks = identifyMessagesWithLinks(messages);

    log.info(
      `Processing ${messagesToProcessParent.length} parent messages and ${messagesToProcessLinks.length} links`,
    );

    // Process related content in parallel
    const [parentMsgs, links] = await Promise.all([
      processParentMessages(messagesToProcessParent, chatId, localDbChat.eCSK),
      processMessageLinks(messagesToProcessLinks, chatId),
    ]);

    // Save related content to local DB
    await saveRelatedContentToLocalDB(parentMsgs, links);

    log.info(`Completed for chat: ${chatId}`);

    // Return summary of reconciliation
    return createSuccessResponse(
      chatId,
      messages.length,
      parentMsgs.length,
      links.length,
    );
  } catch (error) {
    return handleReconciliationError(error, chatId);
  }
};

/*******************
 * UTILS
 *******************/

/**
 * Type definitions for reconciliation responses
 */
type ReconciliationResponse =
  | { status: "not_found" | "no_messages"; chatId: string }
  | {
      status: "success";
      chatId: string;
      messagesProcessed: number;
      parentMessagesProcessed: number;
      linksProcessed: number;
    };

/**
 * Validates user authentication for message reconciliation
 * @throws Error if authentication is missing
 */
const validateAuthentication = (): void => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    log.error("Authentication missing");
    throw new Error("Authentication required for message reconciliation");
  }
};

/**
 * Retrieves chat data from the local database
 *
 * @param chatId - The ID of the chat to retrieve
 * @returns The chat data or undefined if not found
 */
const getChatDetails = async (chatId: string) => {
  const localDbChat = await getChatFromLocalDB(chatId);
  if (!localDbChat) {
    log.info(`Chat ${chatId} not found in local database`);
  }
  return localDbChat;
};

/**
 * Fetches messages for a chat from the server
 *
 * @param chatId - The ID of the chat
 * @param chatSecretKey - The encrypted chat secret key
 * @returns Array of messages
 */
const fetchMessages = async (
  chatId: string,
  chatSecretKey: string,
): Promise<MessageEntity[]> => {
  log.info(`Fetching messages for chat: ${chatId}`);
  const messages = await umbra_processMessages({
    chatId,
    eCSK: chatSecretKey,
    // take: TAKE,
  });

  if (messages.length === 0) {
    log.info(`No messages found for chat: ${chatId}`);
  }

  return messages;
};

/**
 * Saves messages to the local database and updates chat metadata
 *
 * @param messages - The messages to save
 * @param chatId - The ID of the chat
 */
const saveMessagesToLocalDB = async (
  messages: MessageEntity[],
  chatId: string,
): Promise<void> => {
  await addMessagesToLocalDB(messages);
  log.info(`Added ${messages.length} messages to local DB`);

  // Mark the first loading phase as complete
  if (messages.length > 0) {
    await updateChatInLocalDB(chatId, {
      firstMessageId: messages.at(-1)!.id,
    });
  }
};

/**
 * Identifies messages that need parent processing
 *
 * @param messages - The messages to analyze
 * @returns Array of messages that need parent processing
 */
const identifyMessagesNeedingParentProcessing = (
  messages: MessageEntity[],
): MessageEntity[] => {
  return messages.filter(
    (message) =>
      message.isReply &&
      !messages.some(
        (m) =>
          m.id === message.parentMessage?.id &&
          m.timestamp === message.parentMessage?.timestamp,
      ),
  );
};

/**
 * Identifies messages with links that need processing
 *
 * @param messages - The messages to analyze
 * @returns Array of messages with links
 */
const identifyMessagesWithLinks = (
  messages: MessageEntity[],
): MessageEntity[] => {
  return messages.filter((message) => Boolean(message.content.link));
};

/**
 * Processes parent messages in parallel
 *
 * @param messagesToProcess - Messages that need parent processing
 * @param chatId - The ID of the chat
 * @param chatSecretKey - The encrypted chat secret key
 * @returns Array of processed parent messages
 */
const processParentMessages = async (
  messagesToProcess: MessageEntity[],
  chatId: string,
  chatSecretKey: string,
): Promise<MessageEntity[]> => {
  if (messagesToProcess.length === 0) {
    return [];
  }

  const parentMsgPromises = messagesToProcess.map((message) =>
    umbra_processParentMessage({
      messageId: message.parentMessage?.id ?? "",
      // category: message.parentMessage?.category ?? 0,
      chatId,
      eCSK: chatSecretKey,
    }),
  );

  const parentResults = await Promise.all(parentMsgPromises);
  return parentResults.filter(Boolean) as MessageEntity[];
};

/**
 * Processes message links in parallel
 *
 * @param messagesToProcess - Messages with links to process
 * @param chatId - The ID of the chat
 * @returns Array of processed link entities
 */
const processMessageLinks = async (
  messagesToProcess: MessageEntity[],
  chatId: string,
): Promise<LinkEntity[]> => {
  if (messagesToProcess.length === 0) {
    return [];
  }

  const linkPromises = messagesToProcess.map((message) =>
    processMessageLink({
      messageId: message.id,
      chatId,
      timestamp: message.timestamp,
      link: message.content.link ?? "",
      withMetadata: true,
    }),
  );

  const linkResults = await Promise.all(linkPromises);
  return linkResults.filter(Boolean) as LinkEntity[];
};

/**
 * Saves related content (parent messages and links) to the local database
 *
 * @param parentMsgs - Parent messages to save
 * @param links - Links to save
 */
const saveRelatedContentToLocalDB = async (
  parentMsgs: MessageEntity[],
  links: LinkEntity[],
): Promise<void> => {
  if (parentMsgs.length > 0) {
    await addMessagesToLocalDB(parentMsgs);
    log.info(`Added ${parentMsgs.length} parent messages to local DB`);
  }

  if (links.length > 0) {
    await addLinksToLocalDB(links);
    log.info(`Added ${links.length} links to local DB`);
  }
};

/**
 * Creates a success response with reconciliation summary
 *
 * @param chatId - The ID of the chat
 * @param messagesCount - Number of messages processed
 * @param parentMessagesCount - Number of parent messages processed
 * @param linksCount - Number of links processed
 * @returns Success response object
 */
const createSuccessResponse = (
  chatId: string,
  messagesCount: number,
  parentMessagesCount: number,
  linksCount: number,
): ReconciliationResponse => {
  return {
    status: "success",
    chatId,
    messagesProcessed: messagesCount,
    parentMessagesProcessed: parentMessagesCount,
    linksProcessed: linksCount,
  };
};

/**
 * Handles errors during reconciliation
 *
 * @param error - The error that occurred
 * @param chatId - The ID of the chat
 * @throws The original error or a wrapped error
 */
const handleReconciliationError = (error: unknown, chatId: string): never => {
  log.error(`Failed for chat ${chatId}`, error);

  throw error instanceof Error ? error : new Error("Cannot load message list.");
};
